/**
 * DailyIskFlow — "¿cuánto gané hoy (UTC)?".
 *
 * Tile prominente para el Dashboard. Combina:
 *  - Mining ledger del día (ya valuado a Jita buy en /api/mining/ledger)
 *  - Journal positivo del día (market sells + bounties + mission rewards)
 *
 * Diseño:
 *  - Hero number: net_today en amber accent (el número que el industrialist
 *    mira apenas abre la app)
 *  - Breakdown: 2 sub-tiles (Mining / Income) con ISK + sub-text
 *  - Top contributor chips (top ore + top ref_type) para "¿de dónde vino?"
 *  - Banner si alguna fuente falló (note del backend)
 *
 * "todo automatizado": el user abre el dashboard y ve su income del día
 * sin tener que pensar. Refresca cada vez que el dashboard re-renderiza
 * (TanStack Query staleTime por default = 0).
 *
 * Caveats:
 *  - "Hoy" = UTC. Para chars de EVE (UTC) coincide con su día operativo.
 *  - NO resta expenses — gross income por diseño.
 *  - mining.total_value usa ESI market history (sell avg), ~3-10% optimista
 *    vs buy orders reales. Directional OK.
 */

import { useQuery } from '@tanstack/react-query'
import { api, formatISK, formatISKShort } from '../api'

export function DailyIskFlow() {
  const { data, isLoading, isError, error, dataUpdatedAt } = useQuery({
    queryKey: ['dashboard-today'],
    queryFn: () => api.dashboardToday(),
    // Refresca en cada focus del dashboard (default 0 stale time).
    refetchOnWindowFocus: true,
  })

  if (isError) {
    return (
      <div className="bg-eve-danger/10 border border-eve-danger/30 rounded-md p-3 text-eve-danger text-sm">
        Error cargando income de hoy: {(error as Error).message}
      </div>
    )
  }

  if (isLoading || !data) {
    return (
      <div className="bg-eve-panel border border-eve-border rounded-md p-5">
        <div className="h-3 w-1/3 bg-eve-border/40 rounded animate-pulse" />
        <div className="h-10 w-1/2 bg-eve-border/30 rounded animate-pulse mt-2" />
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div className="h-12 bg-eve-border/30 rounded animate-pulse" />
          <div className="h-12 bg-eve-border/30 rounded animate-pulse" />
        </div>
      </div>
    )
  }

  const hasMining = data.mining.isk_today > 0 || data.mining.units_today > 0
  const hasIncome = data.income.isk_today > 0
  const isQuiet = !hasMining && !hasIncome

  return (
    <div
      className={`bg-gradient-to-br from-eve-panel to-eve-bg border rounded-md p-5 ${
        isQuiet ? 'border-eve-border' : 'border-eve-accent/30 shadow-gold'
      }`}
    >
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="text-eve-muted text-[10px] uppercase tracking-widest font-medium">
            Income hoy {data.date}
          </div>
          <div className="font-display text-3xl sm:text-4xl font-bold text-eve-accent mt-1">
            {formatISKShort(data.net_today)}
            <span className="text-base ml-2 font-mono text-eve-muted">ISK</span>
          </div>
          <div className="text-xs text-eve-muted mt-1 italic">
            {isQuiet
              ? 'Día tranquilo — sin actividad registrada aún'
              : 'mining + market income (gross)'}
          </div>
        </div>
      </div>

      {/* Breakdown */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <SourceTile
          label="Mining"
          isk={data.mining.isk_today}
          hint={
            data.mining.ores_mined > 0
              ? `${data.mining.ores_mined} ore${
                  data.mining.ores_mined !== 1 ? 's' : ''
                } · ${data.mining.units_today.toLocaleString('en-US')} unidades`
              : 'sin actividad'
          }
          topItem={
            data.mining.top_ore
              ? {
                  label: data.mining.top_ore.name,
                  value: formatISKShort(data.mining.top_ore.value),
                  sub: `${data.mining.top_ore.quantity.toLocaleString('en-US')} und`,
                }
              : null
          }
        />
        <SourceTile
          label="Market / Otros"
          isk={data.income.isk_today}
          hint={
            data.income.transactions_today > 0
              ? `${data.income.transactions_today} transacciones positivas`
              : 'sin entradas positivas'
          }
          topItem={
            data.income.top_ref_type
              ? {
                  label: data.income.top_ref_type.ref_type,
                  value: formatISKShort(data.income.top_ref_type.total),
                  sub: `${data.income.top_ref_type.count}×`,
                }
              : null
          }
        />
      </div>

      {/* Note banner si data parcial */}
      {data.note && (
        <div className="mt-3 text-xs text-eve-warn italic border-t border-eve-border/40 pt-2">
          {data.note}
        </div>
      )}

      {/* Footer discreto con timestamp */}
      <div className="mt-2 text-[10px] text-eve-muted-2 font-mono">
        ref {dataUpdatedAt ? new Date(dataUpdatedAt).toLocaleTimeString() : '—'} UTC · gross income · no resta expenses
      </div>
    </div>
  )
}

function SourceTile({
  label,
  isk,
  hint,
  topItem,
}: {
  label: string
  isk: number
  hint: string
  topItem: { label: string; value: string; sub: string } | null
}) {
  const positive = isk > 0
  return (
    <div className="bg-eve-bg/40 border border-eve-border/60 rounded-md p-3">
      <div className="text-[10px] uppercase tracking-wider text-eve-muted">
        {label}
      </div>
      <div
        className={`font-mono text-lg font-semibold mt-0.5 ${
          positive ? 'text-eve-text' : 'text-eve-muted-2'
        }`}
      >
        {formatISK(isk, 0)}
        <span className="text-[10px] text-eve-muted ml-1.5">ISK</span>
      </div>
      <div className="text-[10px] text-eve-muted mt-0.5">{hint}</div>
      {topItem && (
        <div className="mt-2 flex items-baseline justify-between gap-2 text-xs">
          <span className="text-eve-text truncate" title={topItem.label}>
            {topItem.label}
          </span>
          <span className="font-mono text-eve-accent shrink-0">
            {topItem.value}
            <span className="text-[10px] text-eve-muted ml-1">{topItem.sub}</span>
          </span>
        </div>
      )}
    </div>
  )
}
